<script lang="ts">
  /**
   * ProjectsPanel.svelte - Panneau de navigation des projets et presets
   *
   * Affiche la liste hiérarchique des projets avec possibilité
   * de sélection, création, modification et suppression.
   * Inclut un mode presets accessible via le menu kebab.
   */

  import { onMount } from 'svelte';
  import {
    projects,
    selectedProject,
    presets,
    selectedPreset,
    presetMode,
    appActions,
    loadingState,
    errors
  } from '../../stores/appStore';
  import { i18n } from '../../stores/i18nStore';
  import type { Project, ProjectType, Preset, CreateTemplateInput, TemplateType, TemplateSection } from '../../types';
  import JSZip from 'jszip';

  // Types
  type ProjectWithStats = Project & { docCount?: number };
  type ViewMode = 'projects' | 'presets';

  interface ContextMenuPosition {
    x: number;
    y: number;
  }

  // View mode
  let viewMode: ViewMode = 'projects';

  // Project state
  let searchQuery: string = '';
  let showNewProjectForm: boolean = false;
  let newProjectTitle: string = '';
  let newProjectType: ProjectType = 'book';
  let contextMenuProject: Project | null = null;
  let contextMenuPosition: ContextMenuPosition = { x: 0, y: 0 };

  // Preset state
  let presetSearchQuery: string = '';
  let showNewPresetForm: boolean = false;
  let newPresetName: string = '';
  let contextMenuPreset: Preset | null = null;

  // Kebab menu state
  let showKebabMenu: boolean = false;

  // Import/export state
  let presetFileInputRef: HTMLInputElement;
  let isImporting: boolean = false;

  // Filtered lists
  $: filteredProjects = $projects.filter(project =>
    project.title.toLowerCase().includes(searchQuery.toLowerCase())
  ) as ProjectWithStats[];

  $: filteredPresets = $presets.filter(preset =>
    preset.name.toLowerCase().includes(presetSearchQuery.toLowerCase())
  );

  // Load data on mount
  onMount(async () => {
    if ($projects.length === 0) {
      await appActions.loadProjects();
    }
  });

  // ========================================================================
  // PROJECT HANDLERS
  // ========================================================================

  async function handleSelectProject(project: Project): Promise<void> {
    // Exit preset mode when selecting a project
    presetMode.set(false);
    selectedPreset.set(null);
    await appActions.selectProject(project);
    contextMenuProject = null;
  }

  async function handleCreateProject(): Promise<void> {
    if (!newProjectTitle.trim()) return;

    try {
      const newProject = await appActions.createProject({
        title: newProjectTitle.trim(),
        projectType: newProjectType,
      });

      presetMode.set(false);
      selectedPreset.set(null);
      await appActions.selectProject(newProject);

      if (newProjectType === 'article') {
        const doc = await appActions.createDoc({
          projectId: newProject.id,
          title: newProjectTitle.trim(),
          documentType: 'bodymatter',
        });
        appActions.selectDoc(doc);
      }

      newProjectTitle = '';
      newProjectType = 'book';
      showNewProjectForm = false;
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  }

  function handleCancelCreate(): void {
    newProjectTitle = '';
    newProjectType = 'book';
    showNewProjectForm = false;
  }

  function handleProjectContextMenu(event: MouseEvent, project: Project): void {
    event.preventDefault();
    contextMenuProject = project;
    contextMenuPreset = null;
    contextMenuPosition = { x: event.clientX, y: event.clientY };
  }

  async function handleEditProject(): Promise<void> {
    if (!contextMenuProject) return;

    const newTitle = prompt($i18n('projects.editPrompt'), contextMenuProject.title);
    if (newTitle && newTitle.trim()) {
      try {
        await appActions.updateProject(contextMenuProject.id, {
          title: newTitle.trim()
        });
      } catch (error) {
        console.error('Failed to update project:', error);
      }
    }

    closeContextMenu();
  }

  async function handleDeleteProject(): Promise<void> {
    if (!contextMenuProject) return;

    const confirmed = confirm($i18n('projects.deleteConfirm', { title: contextMenuProject.title }));
    if (confirmed) {
      try {
        await appActions.deleteProject(contextMenuProject.id);
      } catch (error) {
        console.error('Failed to delete project:', error);
      }
    }

    closeContextMenu();
  }

  // ========================================================================
  // PRESET HANDLERS
  // ========================================================================

  async function switchToPresets(): Promise<void> {
    viewMode = 'presets';
    showKebabMenu = false;
    await appActions.loadPresets();
  }

  function switchToProjects(): void {
    viewMode = 'projects';
    showNewPresetForm = false;
  }

  function handleSelectPreset(preset: Preset): void {
    appActions.selectPreset(preset);
    contextMenuPreset = null;
  }

  async function handleCreatePreset(): Promise<void> {
    if (!newPresetName.trim()) return;

    try {
      const preset = await appActions.createPreset({
        name: newPresetName.trim(),
      });
      appActions.selectPreset(preset);

      newPresetName = '';
      showNewPresetForm = false;
    } catch (error) {
      console.error('Failed to create preset:', error);
    }
  }

  function handleCancelCreatePreset(): void {
    newPresetName = '';
    showNewPresetForm = false;
  }

  function handlePresetContextMenu(event: MouseEvent, preset: Preset): void {
    event.preventDefault();
    contextMenuPreset = preset;
    contextMenuProject = null;
    contextMenuPosition = { x: event.clientX, y: event.clientY };
  }

  async function handleRenamePreset(): Promise<void> {
    if (!contextMenuPreset) return;

    const newName = prompt($i18n('presets.renamePrompt'), contextMenuPreset.name);
    if (newName && newName.trim()) {
      try {
        await appActions.updatePreset(contextMenuPreset.id, {
          name: newName.trim()
        });
      } catch (error) {
        console.error('Failed to rename preset:', error);
      }
    }

    closeContextMenu();
  }

  async function handleDeletePreset(): Promise<void> {
    if (!contextMenuPreset) return;

    const confirmed = confirm($i18n('presets.deleteConfirm', { name: contextMenuPreset.name }));
    if (confirmed) {
      try {
        await appActions.deletePreset(contextMenuPreset.id);
      } catch (error) {
        console.error('Failed to delete preset:', error);
      }
    }

    closeContextMenu();
  }

  // ========================================================================
  // PRESET IMPORT/EXPORT
  // ========================================================================

  function mimeFromExt(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop() || '';
    const mimeMap: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      webp: 'image/webp', svg: 'image/svg+xml',
      css: 'text/css', lua: 'text/x-lua', csl: 'application/xml',
      ttf: 'font/ttf', otf: 'font/otf', woff: 'font/woff', woff2: 'font/woff2',
      indd: 'application/x-indesign', json: 'application/json',
      md: 'text/markdown', yaml: 'text/yaml', yml: 'text/yaml'
    };
    return mimeMap[ext] || 'application/octet-stream';
  }

  function handleImportClick(): void {
    presetFileInputRef?.click();
  }

  async function handleImportPreset(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    isImporting = true;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      // Find preset.json (may be at root or inside a subfolder)
      let presetJsonEntry = zip.file('preset.json');
      let basePath = '';
      if (!presetJsonEntry) {
        // Look for preset.json inside a subfolder (e.g., Rupture/preset.json)
        for (const [path, entry] of Object.entries(zip.files)) {
          if (!entry.dir && path.endsWith('/preset.json')) {
            presetJsonEntry = entry;
            basePath = path.replace('preset.json', '');
            break;
          }
        }
      }

      if (!presetJsonEntry) {
        alert($i18n('presets.importError') + ': preset.json not found');
        return;
      }

      const presetJsonText = await presetJsonEntry.async('text');
      const presetData = JSON.parse(presetJsonText);

      // Derive preset name from JSON or folder name or filename
      const presetName = presetData.name
        || (basePath ? basePath.replace(/\/$/, '').split('/').pop() : null)
        || file.name.replace(/\.zip$/i, '')
        || 'Imported Preset';

      // Create the preset in IndexedDB
      const preset = await appActions.createPreset({
        name: presetName,
        mapping: presetData.mapping ? JSON.stringify(presetData.mapping) : undefined,
        typography: presetData.typography ? JSON.stringify(presetData.typography) : undefined,
        pagelayout: presetData.pagelayout ? JSON.stringify(presetData.pagelayout) : undefined,
        grep: presetData.grep ? JSON.stringify(presetData.grep) : undefined,
        export: presetData.export ? JSON.stringify(presetData.export) : undefined,
        general: presetData.general ? JSON.stringify(presetData.general) : undefined,
        metadata: presetData.metadata ? JSON.stringify(presetData.metadata) : undefined,
      });

      // Also save configs as Template objects (so ProjectConfigPanel can load them)
      const configMap: Record<string, { filename: string; data: any }> = {};
      if (presetData.mapping) configMap['mapping'] = { filename: 'mapping.json', data: presetData.mapping };
      if (presetData.typography) configMap['typography'] = { filename: 'typography.json', data: presetData.typography };
      if (presetData.pagelayout) configMap['pagelayout'] = { filename: 'pagelayout.json', data: presetData.pagelayout };
      if (presetData.grep) configMap['grep'] = { filename: 'grep.json', data: presetData.grep };
      if (presetData.export) configMap['export'] = { filename: 'export.json', data: presetData.export };
      if (presetData.general) configMap['general'] = { filename: 'general.json', data: presetData.general };

      for (const [, cfg] of Object.entries(configMap)) {
        const content = JSON.stringify(cfg.data, null, '\t');
        await appActions.addTemplate({
          projectId: '',
          presetId: preset.id,
          filename: cfg.filename,
          content,
          templateType: 'config',
          templateSection: 'config',
          mimeType: 'application/json',
          size: new Blob([content]).size
        });
      }

      // Import template files from the ZIP
      const templateBase = basePath + 'template/';
      for (const [path, entry] of Object.entries(zip.files)) {
        if (entry.dir || !path.startsWith(templateBase)) continue;
        const relativePath = path.substring(templateBase.length);
        const filename = relativePath.split('/').pop() || '';
        if (!filename) continue;

        let tplType: TemplateType;
        let tplSection: TemplateSection;
        let isBinary = false;

        if (relativePath.startsWith('indesign/') && filename.endsWith('.indd')) {
          tplType = 'indesign'; tplSection = 'indesign'; isBinary = true;
        } else if (relativePath.startsWith('epub/fonts/')) {
          tplType = 'font'; tplSection = 'fonts'; isBinary = true;
        } else if (relativePath.startsWith('epub/style/')) {
          tplType = 'css'; tplSection = 'undefined';
        } else if (relativePath.startsWith('epub/lua/')) {
          tplType = 'lua'; tplSection = 'luafilters';
        } else if (relativePath.startsWith('csl/')) {
          tplType = 'csl'; tplSection = 'csl';
        } else if (relativePath.startsWith('preparo/')) {
          tplType = 'preparo'; tplSection = 'preparo';
        } else {
          tplType = 'other'; tplSection = 'undefined';
        }

        const content = isBinary
          ? await entry.async('base64')
          : await entry.async('text');
        const size = (await entry.async('uint8array')).length;

        await appActions.addTemplate({
          projectId: '',
          presetId: preset.id,
          filename,
          templateType: tplType,
          templateSection: tplSection,
          mimeType: mimeFromExt(filename),
          content,
          size
        });
      }

      // Select the imported preset
      await appActions.selectPreset(preset);

    } catch (error) {
      console.error('Failed to import preset:', error);
      alert($i18n('presets.importError'));
    } finally {
      isImporting = false;
      if (input) input.value = '';
    }
  }

  async function handleExportPreset(): Promise<void> {
    const preset = contextMenuPreset;
    closeContextMenu();
    if (!preset) return;

    try {
      // Load preset's templates
      const presetTemplates = await appActions.loadPresetTemplates(preset.id);

      const zip = new JSZip();

      // Build preset.json from stored Preset fields + config templates
      const presetJson: Record<string, any> = {
        name: preset.name,
        created: preset.created,
        templatePath: '__bundled__',
      };

      // Add config sections from Preset fields or template objects
      const configFiles = ['mapping', 'typography', 'pagelayout', 'grep', 'export', 'general', 'metadata'];
      for (const key of configFiles) {
        // Try from Preset model fields first
        const presetField = (preset as any)[key];
        if (presetField) {
          try {
            presetJson[key] = JSON.parse(presetField);
          } catch { /* ignore parse errors */ }
        }
      }

      // Override with config template content (more authoritative since user edits through config panel)
      const configTemplateMap: Record<string, string> = {
        'mapping.json': 'mapping',
        'typography.json': 'typography',
        'pagelayout.json': 'pagelayout',
        'grep.json': 'grep',
        'export.json': 'export',
        'general.json': 'general',
      };
      for (const tpl of presetTemplates) {
        const configKey = configTemplateMap[tpl.filename];
        if (configKey && tpl.content) {
          try {
            presetJson[configKey] = JSON.parse(tpl.content);
          } catch { /* ignore parse errors */ }
        }
      }

      zip.file('preset.json', JSON.stringify(presetJson, null, 2));

      // Add template files organized by type
      const templateFolder = zip.folder('template');
      if (!templateFolder) return;

      const templateEpubStyleFolder = templateFolder.folder('epub')?.folder('style');
      const templateEpubFontsFolder = templateFolder.folder('epub')?.folder('fonts');
      const templateEpubLuaFolder = templateFolder.folder('epub')?.folder('lua');
      const templateIndesignFolder = templateFolder.folder('indesign');
      const templateCslFolder = templateFolder.folder('csl');
      const templatePreparoFolder = templateFolder.folder('preparo');

      for (const tpl of presetTemplates) {
        // Skip config templates (already in preset.json)
        if (configTemplateMap[tpl.filename]) continue;

        const isBinary = ['font', 'indesign'].includes(tpl.templateType);

        switch (tpl.templateType) {
          case 'css':
            templateEpubStyleFolder?.file(tpl.filename, tpl.content);
            break;
          case 'font':
            templateEpubFontsFolder?.file(tpl.filename, tpl.content, { base64: true });
            break;
          case 'lua':
            templateEpubLuaFolder?.file(tpl.filename, tpl.content);
            break;
          case 'indesign':
            if (tpl.filename.endsWith('.indd')) {
              templateIndesignFolder?.file(tpl.filename, tpl.content, { base64: true });
            }
            break;
          case 'csl':
            templateCslFolder?.file(tpl.filename, tpl.content);
            break;
          case 'preparo':
            templatePreparoFolder?.file(tpl.filename, tpl.content);
            break;
          default:
            if (isBinary) {
              templateFolder.file(tpl.filename, tpl.content, { base64: true });
            } else {
              templateFolder.file(tpl.filename, tpl.content);
            }
        }
      }

      // Generate and download the ZIP
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${preset.name.replace(/[^a-zA-Z0-9àâäéèêëïîôùûüç\s_-]/gi, '').replace(/\s+/g, '-')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export preset:', error);
      alert($i18n('presets.exportError'));
    }
  }

  // ========================================================================
  // SHARED HANDLERS
  // ========================================================================

  function closeContextMenu(): void {
    contextMenuProject = null;
    contextMenuPreset = null;
  }

  function handleClickOutside(_event: MouseEvent): void {
    if (contextMenuProject || contextMenuPreset) {
      closeContextMenu();
    }
    if (showKebabMenu) {
      showKebabMenu = false;
    }
  }

  function toggleKebabMenu(event: MouseEvent): void {
    event.stopPropagation();
    showKebabMenu = !showKebabMenu;
  }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="projects-panel">
  <!-- Header -->
  <div class="panel-header">
    {#if viewMode === 'projects'}
      <h2 class="panel-title">{$i18n('projects.title')}</h2>
      <div class="header-actions">
        <button
          class="btn-icon btn-add"
          on:click={() => showNewProjectForm = !showNewProjectForm}
          title={$i18n('projects.newProject')}
        >
          {showNewProjectForm ? '✕' : '➕'}
        </button>
        <div class="kebab-wrapper">
          <button
            class="btn-icon btn-kebab"
            on:click={toggleKebabMenu}
            title={$i18n('projects.menu')}
          >
            ⋮
          </button>
          {#if showKebabMenu}
            <div class="kebab-dropdown">
              <button class="kebab-item" on:click={switchToPresets}>
                {$i18n('presets.showPresets')}
              </button>
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <h2 class="panel-title">{$i18n('presets.title')}</h2>
      <div class="header-actions">
        <button
          class="btn-icon btn-import"
          on:click={handleImportClick}
          disabled={isImporting}
          title={$i18n('presets.import')}
        >
          {isImporting ? '⏳' : '📥'}
        </button>
        <button
          class="btn-icon btn-add"
          on:click={() => showNewPresetForm = !showNewPresetForm}
          title={$i18n('presets.newPreset')}
        >
          {showNewPresetForm ? '✕' : '➕'}
        </button>
        <button
          class="btn-icon btn-back"
          on:click={switchToProjects}
          title={$i18n('presets.backToProjects')}
        >
          ←
        </button>
      </div>
    {/if}
  </div>

  {#if viewMode === 'projects'}
    <!-- Search bar -->
    <div class="search-bar">
      <input
        type="text"
        class="search-input"
        placeholder="{$i18n('projects.searchPlaceholder')}"
        bind:value={searchQuery}
      />
    </div>

    <!-- New project form -->
    {#if showNewProjectForm}
      <div class="new-project-form">
        <input
          type="text"
          class="form-input"
          placeholder={$i18n('projects.projectTitle')}
          bind:value={newProjectTitle}
          on:keydown={(e) => e.key === 'Enter' && handleCreateProject()}
        />
        <select
          class="form-select"
          bind:value={newProjectType}
        >
          <option value="book">{$i18n('projects.typeBook')}</option>
          <option value="article">{$i18n('projects.typeArticle')}</option>
        </select>
        <div class="form-actions">
          <button
            class="btn btn-primary"
            on:click={handleCreateProject}
            disabled={!newProjectTitle.trim()}
          >
            {$i18n('projects.create')}
          </button>
          <button
            class="btn btn-secondary"
            on:click={handleCancelCreate}
          >
            {$i18n('projects.cancel')}
          </button>
        </div>
      </div>
    {/if}

    <!-- Projects list -->
    <div class="projects-list">
      {#if $loadingState.projects}
        <div class="loading-state">
          <div class="spinner"></div>
          <p>{$i18n('projects.loading')}</p>
        </div>
      {:else if $errors.projects}
        <div class="error-state">
          <p>{$errors.projects}</p>
          <button
            class="btn btn-primary"
            on:click={() => appActions.loadProjects()}
          >
            {$i18n('projects.retry')}
          </button>
        </div>
      {:else if filteredProjects.length === 0}
        <div class="empty-state">
          {#if searchQuery}
            <p>{$i18n('projects.noProjectsFound', { query: searchQuery })}</p>
          {:else}
            <p>{$i18n('projects.noProjectsYet')}</p>
            <button
              class="btn btn-primary"
              on:click={() => showNewProjectForm = true}
            >
              {$i18n('projects.createFirst')}
            </button>
          {/if}
        </div>
      {:else}
        {#each filteredProjects as project (project.id)}
          <div
            class="project-item"
            class:selected={$selectedProject?.id === project.id}
            on:click={() => handleSelectProject(project)}
            on:contextmenu={(e) => handleProjectContextMenu(e, project)}
          >
            <div class="project-icon">{project.projectType === 'article' ? '📄' : '📁'}</div>
            <div class="project-info">
              <div class="project-title">{project.title}</div>
              {#if project.docCount !== undefined}
                <div class="project-stats">
                  {$i18n('projects.docCount', { count: project.docCount })}
                </div>
              {/if}
            </div>
          </div>
        {/each}
      {/if}
    </div>
  {:else}
    <!-- Presets view -->
    <div class="search-bar">
      <input
        type="text"
        class="search-input"
        placeholder="{$i18n('presets.searchPlaceholder')}"
        bind:value={presetSearchQuery}
      />
    </div>

    <!-- New preset form -->
    {#if showNewPresetForm}
      <div class="new-project-form">
        <input
          type="text"
          class="form-input"
          placeholder={$i18n('presets.presetName')}
          bind:value={newPresetName}
          on:keydown={(e) => e.key === 'Enter' && handleCreatePreset()}
        />
        <div class="form-actions">
          <button
            class="btn btn-primary"
            on:click={handleCreatePreset}
            disabled={!newPresetName.trim()}
          >
            {$i18n('presets.create')}
          </button>
          <button
            class="btn btn-secondary"
            on:click={handleCancelCreatePreset}
          >
            {$i18n('presets.cancel')}
          </button>
        </div>
      </div>
    {/if}

    <!-- Presets list -->
    <div class="projects-list">
      {#if filteredPresets.length === 0}
        <div class="empty-state">
          {#if presetSearchQuery}
            <p>{$i18n('projects.noProjectsFound', { query: presetSearchQuery })}</p>
          {:else}
            <p>{$i18n('presets.noPresetsYet')}</p>
            <button
              class="btn btn-primary"
              on:click={() => showNewPresetForm = true}
            >
              {$i18n('presets.createFirst')}
            </button>
          {/if}
        </div>
      {:else}
        {#each filteredPresets as preset (preset.id)}
          <div
            class="project-item"
            class:selected={$selectedPreset?.id === preset.id}
            on:click={() => handleSelectPreset(preset)}
            on:contextmenu={(e) => handlePresetContextMenu(e, preset)}
          >
            <div class="project-icon">🎨</div>
            <div class="project-info">
              <div class="project-title">{preset.name}</div>
            </div>
          </div>
        {/each}
      {/if}
    </div>
  {/if}

  <!-- Context menu for projects -->
  {#if contextMenuProject}
    <div
      class="context-menu"
      style="left: {contextMenuPosition.x}px; top: {contextMenuPosition.y}px;"
    >
      <button class="context-menu-item" on:click={handleEditProject}>
        {$i18n('projects.edit')}
      </button>
      <button class="context-menu-item danger" on:click={handleDeleteProject}>
        {$i18n('projects.delete')}
      </button>
    </div>
  {/if}

  <!-- Hidden file input for preset ZIP import -->
  <input
    type="file"
    accept=".zip"
    bind:this={presetFileInputRef}
    on:change={handleImportPreset}
    style="display: none;"
  />

  <!-- Context menu for presets -->
  {#if contextMenuPreset}
    <div
      class="context-menu"
      style="left: {contextMenuPosition.x}px; top: {contextMenuPosition.y}px;"
    >
      <button class="context-menu-item" on:click={handleExportPreset}>
        {$i18n('presets.export')}
      </button>
      <button class="context-menu-item" on:click={handleRenamePreset}>
        {$i18n('presets.rename')}
      </button>
      <button class="context-menu-item danger" on:click={handleDeletePreset}>
        {$i18n('presets.delete')}
      </button>
    </div>
  {/if}
</div>

<style>
  .projects-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-background);
  }

  /* Header */
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
  }

  .panel-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .btn-icon {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.4rem 0.6rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
  }

  .btn-icon:hover {
    background: var(--hover-background);
    border-color: var(--accent-color);
  }

  .btn-kebab {
    font-weight: bold;
    letter-spacing: 1px;
  }

  /* Kebab dropdown */
  .kebab-wrapper {
    position: relative;
  }

  .kebab-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: var(--panel-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 140px;
  }

  .kebab-item {
    display: block;
    width: 100%;
    padding: 0.6rem 1rem;
    border: none;
    background: transparent;
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s;
  }

  .kebab-item:hover {
    background: var(--hover-background);
  }

  /* Search bar */
  .search-bar {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
  }

  .search-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    background: var(--panel-background);
    color: var(--text-primary);
  }

  .search-input:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  /* New project/preset form */
  .new-project-form {
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--hover-background);
  }

  .form-input,
  .form-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.875rem;
    font-family: inherit;
    background: var(--panel-background);
    color: var(--text-primary);
  }

  .form-input:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--accent-color);
  }

  .form-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary {
    background: var(--accent-color);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }

  .btn-secondary:hover {
    background: var(--hover-background);
  }

  /* Projects/presets list */
  .projects-list {
    flex: 1;
    overflow-y: auto;
  }

  .project-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);
    transition: background 0.15s;
  }

  .project-item:hover {
    background: var(--hover-background);
  }

  .project-item.selected {
    background: var(--accent-color-light, #dbeafe);
    border-left: 3px solid var(--accent-color);
  }

  .project-icon {
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  .project-info {
    flex: 1;
    min-width: 0;
  }

  .project-title {
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .project-stats {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-top: 0.25rem;
  }

  /* States */
  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    gap: 1rem;
    text-align: center;
    color: var(--text-muted);
  }

  .spinner {
    width: 30px;
    height: 30px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-state {
    color: var(--error-color, #ef4444);
  }

  /* Context menu */
  .context-menu {
    position: fixed;
    background: var(--panel-background);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 150px;
  }

  .context-menu-item {
    display: block;
    width: 100%;
    padding: 0.6rem 1rem;
    border: none;
    background: transparent;
    color: var(--text-primary);
    text-align: left;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s;
  }

  .context-menu-item:hover {
    background: var(--hover-background);
  }

  .context-menu-item.danger {
    color: var(--error-color, #ef4444);
  }

  .context-menu-item.danger:hover {
    background: rgba(239, 68, 68, 0.1);
  }
</style>
